import {
  useAdminStore,
  useFeedbackStore,
  useNotificationStore,
} from '@getmayday/vue-modules';

type WebsocketEventEntityType = 'NOTIFICATION';

type WebsocketEventType = 'CREATE' | 'UPDATE' | 'DELETE';

type MaydayWebsocketEvent = {
  entityType: WebsocketEventEntityType;
  id: string;
  eventType: WebsocketEventType;
};

const entityToAction: Record<WebsocketEventEntityType, string> = {
  NOTIFICATION: 'processNotification',
};

/**
 * Wrapper class to handle everything related to websocket
 * We use Azure WebPubSub service as an abstraction
 * See documentation for more info:
 * https://learn.microsoft.com/en-us/azure/azure-web-pubsub/key-concepts
 *
 */
export default class WebsocketHandler {
  websocketClient: WebSocket | null = null;
  websocketGroups: string[] = [];
  ackId = 0;
  timeoutId: ReturnType<typeof setTimeout> | null = null;
  isActive = false;

  /**
   * Called on @enum {MessageTypesEnum.CREATE_WEBSOCKET} message
   */
  public handleCreateEvent() {
    this.isActive = true;
    this.initConnection();
  }

  /**
   * Called on @enum {MessageTypesEnum.CLOSE_WEBSOCKET} message
   */
  public handleCloseEvent() {
    this.isActive = false;
    this.closeConnection();
  }

  /**
   * Coupled to adminStore
   * Gets websocketUrl and websocketGroups states to open the connection
   *
   * Registers the following websocket events handler :
   * - onmessage
   * - onopen
   */
  private initConnection(): void {
    const { websocketUrl, websocketGroups } = useAdminStore();
    if (!websocketUrl || !websocketGroups?.length) return;

    // Make sure previous websocket client is closed before opening a new one
    this.closeConnection();

    this.websocketClient = new WebSocket(
      websocketUrl,
      'json.webpubsub.azure.v1',
    );
    this.websocketGroups = websocketGroups;

    this.websocketClient.onmessage = (event) => {
      const jsonData = JSON.parse(event.data);
      this.processWebsocketEvent(jsonData.data);
    };

    this.websocketClient.onopen = async () => {
      if (!this.websocketClient) return;

      // Small sleep in case of opopen is fired before readyState is updated to avoid
      // sending messages that will fail
      if (this.websocketClient.readyState !== WebSocket.OPEN) {
        await new Promise((r) => setTimeout(r, 2000));
      }
      try {
        // For the user to receive messages, it has to register to groups
        websocketGroups.forEach((group) => {
          if (!this.websocketClient) return;
          this.websocketClient.send(
            JSON.stringify({
              type: 'joinGroup',
              group,
              ackId: this.ackId++,
            }),
          );
        });
        this.startHealthPolling();
      } catch (error) {
        console.error(error);
        console.error('Error while trying to join group');
      }
    };
  }

  /**
   * Polling method to check for websocket state
   * Fires every 5 seconds
   */
  private startHealthPolling() {
    // Clear previous timeout
    this.clearPollingTimeout();

    // Make sure non active tabs are closed
    if (!this.isActive) {
      return this.closeConnection();
    }

    // Force reconnection
    if (this.websocketClient?.readyState !== WebSocket.OPEN) {
      return this.initConnection();
    }

    this.ping();

    // Restart poller
    const timeoutId = setTimeout(() => {
      this.startHealthPolling();
    }, 5000);

    // Keep reference for future clearTimeout
    this.timeoutId = timeoutId;
  }

  /**
   * Sends a simple joinGroup message to websocket server.
   * After several messages without an ack return, it will automatically set
   * websocket's readyState to Websocket.CLOSED which will trigger initConnection
   */
  private ping() {
    try {
      this.websocketClient?.send(
        JSON.stringify({
          type: 'joinGroup',
          group: this.websocketGroups[0],
          ackId: this.ackId++,
        }),
      );
    } catch (error) {
      console.error(error);
    }
  }

  /**
   * Safely close a websocket connection and clear timeout
   */
  private closeConnection() {
    if (!this.websocketClient) return;

    try {
      this.clearPollingTimeout();
      this.websocketClient.close();
    } catch (error) {
      console.error(error);
      console.error('Unable to close Websocket connection');
    }
  }

  /**
   * Clear timeout
   */
  private clearPollingTimeout() {
    if (!this.timeoutId) return;

    clearTimeout(this.timeoutId);
    this.timeoutId = null;
  }

  /**
   * Generic entry point to the websocket onmessage event handler
   * @param event
   */
  private async processWebsocketEvent(event: MaydayWebsocketEvent) {
    if (!event) return;

    const { entityType } = event;
    const actionName = entityToAction[entityType];

    const action = this[actionName as keyof WebsocketHandler];

    if (!action || typeof action !== 'function') return;

    return (action as any)(event);
  }

  /**
   * Handles websocket notification event
   * @param event
   */
  private async processNotification(event: MaydayWebsocketEvent) {
    const { eventType, id } = event;
    const { getNotificationById, deleteNotificationFromStore } =
      useNotificationStore();
    const { getFeedbacks, getUnreadCommentCount } = useFeedbackStore();

    switch (eventType) {
      case 'CREATE':
        getNotificationById(id, false, true);
        break;
      case 'UPDATE':
        const notification = await getNotificationById(id, false, false);
        if (notification?.metadata?.type === 'TaskComment') {
          getUnreadCommentCount();
          getFeedbacks();
        }
        break;
      case 'DELETE':
        deleteNotificationFromStore(id);

        break;
      default:
        break;
    }
  }
}
